import { usePrivy } from "@privy-io/react-auth";
import { useTransactionSnackbar } from "./useTransactionSnackbar";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import DifferentAccountDialog from "@/components/Dialog/DifferentAccountDialog";

// Add type declarations for wallet providers
declare global {
  interface Window {
    ethereum?: any;
    okxwallet?: any;
  }
}

export default function useDetectDifferentUser() {
  const { user } = usePrivy();
  const { t } = useTranslation();
  const { showTransactionSnackbar } = useTransactionSnackbar();
  const walletAddress = user?.wallet?.address || "";
  const [externalWalletAddress, setExternalWalletAddress] = useState<string>("");
  const [externalWalletName, setExternalWalletName] = useState<string>("");
  const [isDifferentAccount, setIsDifferentAccount] = useState<boolean>(false);
  const [_isExitingDialog, setIsExitingDialog] = useState<boolean>(false);
  const [isAttemptingSwitch, setIsAttemptingSwitch] = useState<boolean>(false);
  
  // Ref to store interval ID for continuous checking
  const checkingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Function to check if various wallets are available
  const getAvailableWallets = useCallback(() => {
    const wallets = [];
    
    // Check for MetaMask
    if (
      typeof window !== "undefined" &&
      typeof window.ethereum !== "undefined" &&
      window.ethereum.isMetaMask === true
    ) {
      wallets.push({ name: "MetaMask", provider: window.ethereum });
    }
    
    // Check for OKX Wallet - try different detection methods
    if (
      typeof window !== "undefined"
    ) {
      // First check for dedicated okxwallet object
      if (typeof window.okxwallet !== "undefined") {
        console.log("OKX Wallet detected via window.okxwallet");
        wallets.push({ name: "OKX Wallet", provider: window.okxwallet });
      } 
      // Then fall back to checking ethereum object for OKX properties
      else if (
        typeof window.ethereum !== "undefined" && 
        (window.ethereum.isOKExWallet === true || window.ethereum.isOKX === true)
      ) {
        console.log("OKX Wallet detected via window.ethereum");
        wallets.push({ name: "OKX Wallet", provider: window.ethereum });
      }
    }
    
    // Check for Coinbase Wallet
    if (
      typeof window !== "undefined" &&
      typeof window.ethereum !== "undefined" &&
      window.ethereum.isCoinbaseWallet === true
    ) {
      wallets.push({ name: "Coinbase Wallet", provider: window.ethereum });
    }
    
    // Check for Trust Wallet
    if (
      typeof window !== "undefined" &&
      typeof window.ethereum !== "undefined" &&
      window.ethereum.isTrustWallet === true
    ) {
      wallets.push({ name: "Trust Wallet", provider: window.ethereum });
    }
    
    // If ethereum is available but no specific wallet identified, add it as Generic
    if (
      typeof window !== "undefined" &&
      typeof window.ethereum !== "undefined" &&
      wallets.length === 0
    ) {
      wallets.push({ name: "Wallet", provider: window.ethereum });
    }
    
    return wallets;
  }, []);

  // Function to get the current wallet account from any available provider
  const getExternalWalletAccount = useCallback(async (): Promise<{ address: string; walletName: string }> => {
    const wallets = getAvailableWallets();
    
    if (wallets.length === 0) {
      return { address: "", walletName: "" };
    }

    try {
      // Try to get account from the first available wallet
      const wallet = wallets[0];
      
      // For OKX Wallet, we might need to handle this case differently
      let accounts;
      
      try {
        // Try getting accounts without triggering wallet UI
        accounts = await wallet.provider.request({
          method: "eth_accounts",
        });
      } catch (e) {
        // If eth_accounts fails, fall back to eth_requestAccounts
        console.log("Failed to get accounts with eth_accounts, trying eth_requestAccounts");
        accounts = await wallet.provider.request({
          method: "eth_requestAccounts",
        });
      }
      
      return { 
        address: accounts[0]?.toLowerCase() || "", 
        walletName: wallet.name 
      };
    } catch (error) {
      console.error("Error getting wallet accounts:", error);
      return { address: "", walletName: "" };
    }
  }, [getAvailableWallets]);

  useEffect(() => {
    // Skip if user is not logged in or no external wallets are available
    if (!walletAddress || getAvailableWallets().length === 0) {
      setIsDifferentAccount(false);
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const checkAndUpdateAccountStatus = async () => {
      const { address: currentWalletAccount, walletName } = await getExternalWalletAccount();
      setExternalWalletAddress(currentWalletAccount);
      setExternalWalletName(walletName);

      // If the address is different and both addresses exist, show the warning
      // Use a slight delay to avoid flickering if accounts are changing rapidly
      const isDifferent =
        currentWalletAccount &&
        walletAddress &&
        currentWalletAccount.toLowerCase() !== walletAddress.toLowerCase();

      clearTimeout(timeoutId);

      if (isDifferent) {
        timeoutId = setTimeout(() => {
          setIsDifferentAccount(true);
        }, 1000); // 1 second delay
      } else {
        setIsDifferentAccount(false);
        // Clear any continuous checking that might be happening
        if (checkingIntervalRef.current) {
          clearInterval(checkingIntervalRef.current);
          checkingIntervalRef.current = null;
          setIsAttemptingSwitch(false);
        }
      }
    };

    // Initial check
    checkAndUpdateAccountStatus();

    // Add event listener for account changes in wallet providers
    const wallets = getAvailableWallets();
    if (wallets.length > 0) {
      const handleAccountsChanged = (accounts: string[]) => {
        const newAccount = accounts[0]?.toLowerCase() || "";
        setExternalWalletAddress(newAccount);

        // Compare with the logged-in address
        const isDifferent =
          newAccount &&
          walletAddress &&
          newAccount !== walletAddress.toLowerCase();

        clearTimeout(timeoutId);

        if (isDifferent) {
          timeoutId = setTimeout(() => {
            setIsDifferentAccount(true);
          }, 1000); // 1 second delay
        } else {
          setIsDifferentAccount(false);
          // Clear any continuous checking that might be happening
          if (checkingIntervalRef.current) {
            clearInterval(checkingIntervalRef.current);
            checkingIntervalRef.current = null;
            setIsAttemptingSwitch(false);
          }
        }
      };

      // Add the listener to the first available wallet
      wallets[0].provider.on("accountsChanged", handleAccountsChanged);

      // Initial check
      checkAndUpdateAccountStatus();

      // Cleanup function
      return () => {
        wallets[0].provider.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );
        clearTimeout(timeoutId);
        // Clear any active interval
        if (checkingIntervalRef.current) {
          clearInterval(checkingIntervalRef.current);
          checkingIntervalRef.current = null;
        }
      };
    }

    // Initial check if no wallet providers
    checkAndUpdateAccountStatus();

    return () => {
      clearTimeout(timeoutId);
      // Clear any active interval
      if (checkingIntervalRef.current) {
        clearInterval(checkingIntervalRef.current);
        checkingIntervalRef.current = null;
      }
    };
  }, [walletAddress, getAvailableWallets, getExternalWalletAccount]);

  // Function to start continuous account checking
  const startContinuousAccountChecking = useCallback(() => {
    // Clear any existing interval
    if (checkingIntervalRef.current) {
      clearInterval(checkingIntervalRef.current);
    }

    // Set state to indicate we're attempting a switch
    setIsAttemptingSwitch(true);

    // Start a new interval to check every 1 second
    checkingIntervalRef.current = setInterval(async () => {
      const { address: currentWalletAccount } = await getExternalWalletAccount();
      setExternalWalletAddress(currentWalletAccount);

      // If accounts match, clear interval and update UI
      if (
        currentWalletAccount &&
        walletAddress &&
        currentWalletAccount.toLowerCase() === walletAddress.toLowerCase()
      ) {
        // Start exit animation
        setIsExitingDialog(true);
        
        // Clear the interval
        if (checkingIntervalRef.current) {
          clearInterval(checkingIntervalRef.current);
          checkingIntervalRef.current = null;
        }

        // Show success message
        showTransactionSnackbar({
          status: "success",
          title: t("wallet.success", "Success"),
          message: t("wallet.account_switched", "Account switched successfully"),
        });

        // Close dialog after animation completes
        setTimeout(() => {
          setIsDifferentAccount(false);
          setIsExitingDialog(false);
          setIsAttemptingSwitch(false);
        }, 300);
      }
    }, 1000);
  }, [getExternalWalletAccount, walletAddress, showTransactionSnackbar, t]);

  // Function to prompt wallet to switch to the correct account
  const promptSwitchToCorrectAccount = useCallback(async (): Promise<void> => {
    const wallets = getAvailableWallets();
    console.log("Available wallets:", wallets);
    
    if (wallets.length === 0 || !walletAddress) {
      showTransactionSnackbar({
        status: "error",
        title: t("wallet.error", "Wallet Error"),
        message: t(
          "wallet.not_available",
          "External wallet is not available or you're not logged in."
        ),
      });
      return Promise.reject(new Error("Wallet not available"));
    }

    try {
      const wallet = wallets[0];
      console.log("Using wallet for account switching:", wallet.name);
      
      // Use wallet-specific methods for account switching
      if (wallet.name === "OKX Wallet") {
        console.log("Using OKX Wallet specific method for switching accounts");
        try {
          // First try with eth_accounts to check current accounts
          const accounts = await wallet.provider.request({
            method: "eth_accounts"
          });
          console.log("Current OKX accounts:", accounts);
          
          // Then request account selection
          await wallet.provider.request({
            method: "eth_requestAccounts",
          });
        } catch (okxError) {
          console.error("OKX specific method failed, trying generic fallback:", okxError);
          // If OKX specific approach fails, try the generic method
          await wallet.provider.request({
            method: "wallet_requestPermissions",
            params: [{ eth_accounts: {} }],
          });
        }
      } else {
        console.log("Using standard wallet_requestPermissions method for switching accounts");
        // For MetaMask and other wallets that support wallet_requestPermissions
        await wallet.provider.request({
          method: "wallet_requestPermissions",
          params: [{ eth_accounts: {} }],
        });
      }
      
      // Start continuous checking for account changes
      startContinuousAccountChecking();
      
      // Show a message that we're waiting for the correct account
      showTransactionSnackbar({
        status: "info",
        title: t("wallet.info", "Account Selection"),
        message: t(
          "wallet.switch_account",
          `Please select the correct account in ${externalWalletName || "your wallet"}.`
        ),
      });
      
      // Return resolved promise but don't close dialog - it will close automatically 
      // when the account changes via the continuous checking
      return Promise.resolve();
      
    } catch (error) {
      console.error(`Error switching ${externalWalletName} account:`, error);
      // Stop continuous checking
      if (checkingIntervalRef.current) {
        clearInterval(checkingIntervalRef.current);
        checkingIntervalRef.current = null;
      }
      setIsAttemptingSwitch(false);
      
      showTransactionSnackbar({
        status: "error",
        title: t("wallet.error", "Error"),
        message: t("wallet.switch_failed", "Failed to switch account."),
      });
      return Promise.reject(error);
    }
  }, [
    walletAddress,
    getAvailableWallets,
    externalWalletName,
    getExternalWalletAccount,
    showTransactionSnackbar,
    t,
    startContinuousAccountChecking,
  ]);

  // Function to close the dialog
  const handleCloseDialog = useCallback(() => {
    // Stop continuous checking if it's active
    if (checkingIntervalRef.current) {
      clearInterval(checkingIntervalRef.current);
      checkingIntervalRef.current = null;
    }
    
    setIsExitingDialog(true);
    
    // Add delay to match animation duration
    setTimeout(() => {
      setIsDifferentAccount(false);
      setIsExitingDialog(false);
      setIsAttemptingSwitch(false);
    }, 300);
  }, []);

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (checkingIntervalRef.current) {
        clearInterval(checkingIntervalRef.current);
        checkingIntervalRef.current = null;
      }
    };
  }, []);

  return {
    walletAddress,
    externalWalletAddress,
    externalWalletName,
    isDifferentAccount,
    isAttemptingSwitch,
    promptSwitchToCorrectAccount,
    handleCloseDialog,
    DifferentAccountDialogComponent: isDifferentAccount ? (
      <DifferentAccountDialog
        open={isDifferentAccount}
        onClose={handleCloseDialog}
        onSwitchAccount={promptSwitchToCorrectAccount}
        connectedAddress={walletAddress}
        metaMaskAddress={externalWalletAddress}
        isAttemptingSwitch={isAttemptingSwitch}
        walletName={externalWalletName}
      />
    ) : null,
  };
}
